import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitCommitHorizontal, Loader2, Sparkles, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type GitProgressReview, type TaskGitProgressReports, shortGitSha } from '@apm/contracts';
import { displayDateTime } from '../../../lib/date';
import { errorMessage } from '../../../lib/error-message';
import { cn } from '../../../lib/utils';
import { progressCommitsApi, progressCommitsKeys } from '../progress-commits-api';
import { useGitPermissions } from '../use-git-permissions';

/**
 * Bản báo commit của một việc. Chỉ người có `git:read` mới hỏi (cùng cửa với khối
 * "Phát triển"): người không có quyền thì không lời gọi `/git/` nào. Đọc cả khi dự
 * án đã gỡ kho — bản báo cũ vẫn là lịch sử của việc (APM-GIT-q).
 */
export function useTaskProgressReports(
  projectId: string,
  taskId: string,
): TaskGitProgressReports | undefined {
  const permissions = useGitPermissions(projectId);
  const reports = useQuery({
    queryKey: progressCommitsKeys.reports(projectId, taskId),
    queryFn: () => progressCommitsApi.reports(projectId, taskId),
    enabled: permissions.read,
    retry: false,
    // Có lượt AI rà đang chạy ngầm (APM-PCRV) ⇒ hỏi lại tới khi xong.
    refetchInterval: (query) =>
      query.state.data?.reports?.some((report) => report.review?.status === 'pending')
        ? 5_000
        : false,
  });
  return Array.isArray(reports.data?.reports) ? reports.data : undefined;
}

/**
 * "Commit đã báo" trên chi tiết việc — APM-PCOM-d, docs/plan-progress-commits.md §4.4.
 *
 * Mỗi bản báo một khối: thao tác (tick mục / hoàn thành), người báo, lúc báo, rồi
 * danh sách commit (link sang máy chủ git) hoặc "Báo không kèm commit". Người báo
 * và PM gỡ được từng commit. Chưa có bản báo nào thì không chiếm chỗ.
 *
 * ⚠️ Không nhận asOf: danh sách tự mang mốc của từng bản báo; Replay dựng nó từ
 * `task.progress_commits_reported`.
 */
export function TaskProgressCommits({
  projectId,
  taskId,
  className,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly className?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const client = useQueryClient();
  const data = useTaskProgressReports(projectId, taskId);
  const remove = useMutation({
    mutationFn: (input: { reportId: string; repositoryId: string; sha: string }) =>
      progressCommitsApi.remove(projectId, taskId, input.reportId, input.repositoryId, input.sha),
    onSuccess: (next) => {
      client.setQueryData(progressCommitsKeys.reports(projectId, taskId), next);
      toast.success(t('progressCommits.block.removed'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!data || data.reports.length === 0) return null;

  return (
    <section
      aria-label={t('progressCommits.block.title')}
      data-testid="task-progress-commits"
      className={cn('flex min-w-0 flex-col gap-2', className)}
    >
      <h3 className="text-xs font-medium text-muted-foreground">
        {t('progressCommits.block.title')}
      </h3>
      <ul className="flex flex-col gap-2">
        {data.reports.map((report) => (
          <li
            key={report.id}
            className="flex flex-col gap-1.5 rounded-DEFAULT border border-border border-l-4 border-l-primary/60 p-2"
          >
            <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
              <span className="min-w-0 break-words font-medium">
                {report.trigger === 'checklist_item'
                  ? t('progressCommits.block.checklist', { text: report.checklistItemText ?? '' })
                  : t('progressCommits.block.completed')}
              </span>
              <span className="text-muted-foreground">
                {report.createdBy.name} · {displayDateTime(report.createdAt)}
              </span>
            </div>
            {report.skipped || report.commits.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {report.skipped
                  ? t('progressCommits.block.skipped')
                  : t('progressCommits.block.emptied')}
              </p>
            ) : (
              <ul className="flex flex-col gap-1">
                {report.commits.map((commit) => (
                  <li
                    key={`${commit.repositoryId}:${commit.sha}`}
                    className="flex items-start gap-1.5 text-xs"
                  >
                    <GitCommitHorizontal
                      aria-hidden
                      className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="break-words">{commit.title}</span>
                      <span className="flex flex-wrap gap-x-2 text-[11px] text-muted-foreground">
                        {commit.webUrl ? (
                          <a
                            href={commit.webUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono underline underline-offset-2"
                          >
                            {commit.shortSha}
                          </a>
                        ) : (
                          <span className="font-mono">{commit.shortSha}</span>
                        )}
                        <span className="break-all font-mono">{commit.branch}</span>
                        <span>{displayDateTime(commit.committedAt)}</span>
                      </span>
                    </span>
                    {report.canRemove ? (
                      <button
                        type="button"
                        aria-label={t('progressCommits.block.remove', { sha: commit.shortSha })}
                        title={t('progressCommits.block.remove', { sha: commit.shortSha })}
                        disabled={remove.isPending}
                        onClick={() =>
                          remove.mutate({
                            reportId: report.id,
                            repositoryId: commit.repositoryId,
                            sha: commit.sha,
                          })
                        }
                        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <X aria-hidden className="size-3.5" />
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {report.review ? (
              <ReviewLine
                projectId={projectId}
                taskId={taskId}
                reportId={report.id}
                review={report.review}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

const VERDICT_TONE = {
  related: 'border-rag-green/40 bg-rag-green/10 text-rag-green',
  partial: 'border-rag-amber/40 bg-rag-amber/10 text-rag-amber',
  unrelated: 'border-rag-red/40 bg-rag-red/10 text-rag-red',
} as const;

/**
 * Kết quả AI rà commit của một bản báo — APM-PCRV, docs/plan-progress-commit-review.md §8.
 * Chip trạng thái; bấm ⇒ lời tóm tắt, bằng chứng (commit · tệp), AI đã đọc bao nhiêu.
 * PM "Đã xem" (ẩn nhãn trên thẻ); lượt hỏng / bị bỏ qua có "Rà lại". Nhãn "AI gợi ý"
 * nhắc rằng đây là tham khảo — việc không bị đổi gì (luật #6).
 */
function ReviewLine({
  projectId,
  taskId,
  reportId,
  review,
}: {
  readonly projectId: string;
  readonly taskId: string;
  readonly reportId: string;
  readonly review: GitProgressReview;
}): ReactNode {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const after = (next: TaskGitProgressReports) => {
    client.setQueryData(progressCommitsKeys.reports(projectId, taskId), next);
    void client.invalidateQueries({ queryKey: progressCommitsKeys.warnings(projectId) });
    void client.invalidateQueries({ queryKey: progressCommitsKeys.pendingReviews() });
  };
  const acknowledge = useMutation({
    mutationFn: () => progressCommitsApi.acknowledge(projectId, taskId, reportId),
    onSuccess: after,
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const retry = useMutation({
    mutationFn: () => progressCommitsApi.retry(projectId, taskId, reportId),
    onSuccess: after,
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const chip =
    review.status === 'pending' ? (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Loader2 aria-hidden className="size-3 animate-spin" />
        {t('progressCommits.review.status.pending')}
      </span>
    ) : review.status === 'done' && review.verdict ? (
      <span
        className={cn(
          'inline-flex h-5 items-center rounded-full border px-1.5',
          VERDICT_TONE[review.verdict],
        )}
      >
        {t(`progressCommits.review.verdict.${review.verdict}`)}
      </span>
    ) : (
      <span className="text-muted-foreground">
        {t(
          review.status === 'failed'
            ? 'progressCommits.review.status.failed'
            : 'progressCommits.review.status.skipped',
        )}
      </span>
    );

  return (
    <div
      className="flex flex-col gap-1 border-t border-border pt-1.5 text-xs"
      data-testid="progress-review"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Sparkles aria-hidden className="size-3.5 shrink-0 text-primary" />
        <span className="text-muted-foreground">{t('progressCommits.review.label')}</span>
        {review.status === 'done' ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="rounded-full"
            data-testid="progress-review-toggle"
          >
            {chip}
          </button>
        ) : (
          chip
        )}
        {review.acknowledgedBy && review.acknowledgedAt ? (
          <span className="text-muted-foreground">
            {t('progressCommits.review.acknowledged', {
              name: review.acknowledgedBy.name,
              at: displayDateTime(review.acknowledgedAt),
            })}
          </span>
        ) : null}
        <span className="ml-auto flex gap-1">
          {review.canAcknowledge ? (
            <button
              type="button"
              disabled={acknowledge.isPending}
              onClick={() => acknowledge.mutate()}
              className="rounded-full border border-border px-2 py-0.5 glass-hover"
              data-testid="progress-review-acknowledge"
            >
              {t('progressCommits.review.acknowledge')}
            </button>
          ) : null}
          {review.canRetry ? (
            <button
              type="button"
              disabled={retry.isPending}
              onClick={() => retry.mutate()}
              className="rounded-full border border-border px-2 py-0.5 glass-hover"
              data-testid="progress-review-retry"
            >
              {t('progressCommits.review.retry')}
            </button>
          ) : null}
        </span>
      </div>
      {open && review.status === 'done' ? (
        <div className="flex flex-col gap-1 rounded-DEFAULT bg-muted/40 p-2">
          {review.summary ? <p className="break-words">{review.summary}</p> : null}
          {review.evidence.length > 0 ? (
            <ul className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
              {review.evidence.map((item) => (
                <li key={`${item.sha}:${item.path ?? ''}`} className="break-all font-mono">
                  {shortGitSha(item.sha)} · {item.path ?? t('progressCommits.review.commitTitle')}
                </li>
              ))}
            </ul>
          ) : null}
          <p className="text-[11px] text-muted-foreground">
            {t(
              review.basedOn === 'diff'
                ? 'progressCommits.review.basedOn.diff'
                : 'progressCommits.review.basedOn.message',
            )}
            {review.readStats && review.basedOn === 'diff'
              ? ` · ${
                  review.readStats.bytesRead > 0
                    ? t('progressCommits.review.readStats', {
                        read: review.readStats.filesRead,
                        files: review.readStats.files,
                        kb: Math.max(1, Math.ceil(review.readStats.bytesRead / 1024)),
                      })
                    : t('progressCommits.review.mapOnly', { files: review.readStats.files })
                }`
              : ''}
            {' · '}
            {t('progressCommits.review.suggestionOnly')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Số commit đã báo cho từng mục checklist (`checklistItemId` → số) — chip cạnh mục. */
export function commitCountByChecklistItem(
  data: TaskGitProgressReports | undefined,
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const report of data?.reports ?? []) {
    if (report.trigger !== 'checklist_item' || !report.checklistItemId) continue;
    counts.set(
      report.checklistItemId,
      (counts.get(report.checklistItemId) ?? 0) + report.commits.length,
    );
  }
  return counts;
}
